// src/components/mdx/client/visualizations/ChartSelectionExplorer.client.tsx

/**
 * Interactive chart-selection explorer for marketing analytics questions.
 *
 * OPTIMIZATION NOTE: Datasets are generated lazily per selected tab so the blog
 * post does not pay for six chart payloads on initial mount. Recharts roots use
 * the shared `BLOG_CHART_THROTTLE_MS` guard used by other blog visualizations.
 */

'use client'

import { useMemo, useState, useTransition, type KeyboardEvent } from 'react'

import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  LabelList,
  Legend,
  Line,
  ReferenceArea,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis
} from '~/lib/charts/client'
import type { NameType, TooltipContentType, ValueType } from '~/lib/charts/types'
import { getOrCreateTooltipWrapper, type CustomTooltipProps } from '~/lib/charts/tooltip'
import { AXIS_TOKENS, MARKETING_DATA_VIZ_SERIES } from '~/components/mdx/client/visualizations/blogChartPalette'
import { BlogChartShell } from '~/components/mdx/client/visualizations/BlogChartShell.client'
import { BLOG_CHART_THROTTLE_MS, BlogChartPlotArea, BlogChartTooltipShell, BLOG_CHART_LABEL_STYLE, formatBarEndCurrency, getBlogChartMargin } from '~/components/mdx/client/visualizations/blogChartLayout'
import { ChartVisibilityBoundary } from '~/components/mdx/client/visualizations/ChartVisibilityBoundary.client'
import { MarketingFunnelChart } from '~/components/mdx/client/visualizations/MarketingFunnelChart.client'
import { RetentionCurveChart } from '~/components/mdx/client/visualizations/RetentionCurveChart.client'
import {
  cohortRateToColor,
  generateChannelCpaData,
  generateCohortHeatmapData,
  generateMarketingFunnelChartStages,
  generateMerTimeSeries,
  generateSpendEfficiencyData,
  generateVideoRetentionData,
  METRIC_QUESTIONS,
  type MetricQuestionType
} from '~/components/mdx/client/visualizations/marketingDataVizData'
import { useMediaQuery } from '~/lib/hooks/useMediaQuery'
import { cn } from '~/lib/utils'

function ExplorerTooltip({ active, payload, label }: CustomTooltipProps): JSX.Element | null {
  if (!active || !payload?.length) {
    return null
  }

  return (
    <BlogChartTooltipShell title={label !== undefined ? String(label) : undefined}>
      {payload.map((entry, index) => {
        const raw = entry.value
        const formatted =
          typeof raw === 'number'
            ? Number.isInteger(raw) && raw > 999
              ? raw.toLocaleString()
              : raw.toFixed(typeof raw === 'number' && raw % 1 !== 0 ? 2 : 0)
            : raw
        return (
          <p key={`${entry.name ?? 'value'}-${index}`} className="text-gray-300">
            {entry.name}: <span className="font-semibold text-white">{formatted}</span>
          </p>
        )
      })}
    </BlogChartTooltipShell>
  )
}

interface ChartSelectionExplorerContentProps {
  isActive: boolean
}

function ChartSelectionExplorerContent({ isActive }: ChartSelectionExplorerContentProps): JSX.Element {
  const [selected, setSelected] = useState<MetricQuestionType>('time-series')
  const [, startTransition] = useTransition()
  const isMobile = useMediaQuery('(max-width: 768px)')
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
  const animate = isActive && !prefersReducedMotion
  const tooltip = useMemo(() => getOrCreateTooltipWrapper(ExplorerTooltip), [])
  const margin = useMemo(() => {
    switch (selected) {
      case 'time-series':
        return getBlogChartMargin(isMobile, { bottom: isMobile ? 36 : 52, left: isMobile ? 4 : 12 })
      case 'comparison':
        return getBlogChartMargin(isMobile, { right: isMobile ? 44 : 52 })
      case 'funnel':
      case 'retention':
      case 'cohort':
        return getBlogChartMargin(isMobile)
      case 'scatter':
        return getBlogChartMargin(isMobile, { bottom: isMobile ? 34 : 8, right: isMobile ? 16 : 8 })
      default: {
        const exhaustive: never = selected
        void exhaustive
        return getBlogChartMargin(isMobile)
      }
    }
  }, [selected, isMobile])

  const activeQuestion =
    METRIC_QUESTIONS.find(question => question.id === selected) ?? METRIC_QUESTIONS[0] ?? {
      id: 'time-series' as const,
      question: 'How did efficiency trend over time?',
      chartType: 'Line chart',
      example: 'Weekly MER, blended CAC, platform ROAS'
    }

  const merData = useMemo(
    () => (selected === 'time-series' ? generateMerTimeSeries() : null),
    [selected]
  )
  const channelData = useMemo(
    () => (selected === 'comparison' ? generateChannelCpaData() : null),
    [selected]
  )
  const funnelStages = useMemo(
    () => (selected === 'funnel' ? generateMarketingFunnelChartStages() : null),
    [selected]
  )
  const retentionData = useMemo(
    () => (selected === 'retention' ? generateVideoRetentionData() : null),
    [selected]
  )
  const scatterData = useMemo(
    () => (selected === 'scatter' ? generateSpendEfficiencyData() : null),
    [selected]
  )
  const cohortData = useMemo(
    () => (selected === 'cohort' ? generateCohortHeatmapData() : null),
    [selected]
  )

  const cohortMatrix = useMemo(() => {
    if (!cohortData) {
      return { cohorts: [] as string[], months: [] as number[] }
    }
    const cohorts = [...new Set(cohortData.map(cell => cell.cohort))]
    const months = [...new Set(cohortData.map(cell => cell.month))].sort((a, b) => a - b)
    return { cohorts, months }
  }, [cohortData])

  const handleSelect = (id: MetricQuestionType): void => {
    startTransition(() => {
      setSelected(id)
    })
  }

  const handleTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>, id: MetricQuestionType): void => {
    const currentIndex = METRIC_QUESTIONS.findIndex(question => question.id === id)
    if (currentIndex < 0) {
      return
    }

    let nextIndex = currentIndex
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      nextIndex = (currentIndex + 1) % METRIC_QUESTIONS.length
      event.preventDefault()
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      nextIndex = (currentIndex - 1 + METRIC_QUESTIONS.length) % METRIC_QUESTIONS.length
      event.preventDefault()
    } else {
      return
    }

    const next = METRIC_QUESTIONS[nextIndex]
    if (next) {
      handleSelect(next.id)
      requestAnimationFrame(() => {
        document.getElementById(`chart-tab-${next.id}`)?.focus()
      })
    }
  }

  const renderChart = (): JSX.Element => {
    switch (selected) {
      case 'time-series':
        return (
          <ComposedChart data={merData ?? []} margin={margin} accessibilityLayer throttleDelay={BLOG_CHART_THROTTLE_MS}>
            <defs>
              <linearGradient id="explorer-mer-area" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={MARKETING_DATA_VIZ_SERIES.mer} stopOpacity={0.35} />
                <stop offset="95%" stopColor={MARKETING_DATA_VIZ_SERIES.mer} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke={AXIS_TOKENS.grid} strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="week"
              stroke={AXIS_TOKENS.stroke}
              fontSize={isMobile ? 10 : 11}
              ticks={(merData ?? []).map(point => point.week)}
              interval={0}
              tickLine={false}
              tickMargin={8}
              height={isMobile ? 36 : 40}
              label={
                isMobile
                  ? undefined
                  : { value: 'Week', position: 'insideBottom', offset: -6, fill: AXIS_TOKENS.stroke, fontSize: 11 }
              }
            />
            <YAxis
              stroke={AXIS_TOKENS.stroke}
              fontSize={isMobile ? 10 : 12}
              domain={[0, 3.5]}
              tickFormatter={value => Number(value).toFixed(1)}
              label={
                isMobile
                  ? undefined
                  : { value: 'Ratio', angle: -90, position: 'insideLeft', fill: AXIS_TOKENS.stroke, fontSize: 11 }
              }
            />
            <Tooltip content={tooltip as TooltipContentType<ValueType, NameType>} />
            <Legend verticalAlign="top" height={28} wrapperStyle={{ fontSize: isMobile ? '10px' : '12px' }} />
            <ReferenceArea
              x1="W6"
              x2="W12"
              fill={MARKETING_DATA_VIZ_SERIES.mer}
              fillOpacity={0.1}
              stroke={MARKETING_DATA_VIZ_SERIES.mer}
              strokeOpacity={0.25}
              label={{
                value: 'Post creative refresh',
                position: 'insideTopLeft',
                fill: MARKETING_DATA_VIZ_SERIES.mer,
                fontSize: 10
              }}
            />
            <Area
              type="monotone"
              dataKey="mer"
              legendType="none"
              fill="url(#explorer-mer-area)"
              stroke="none"
              animationDuration={animate ? 700 : 0}
            />
            <Line
              type="monotone"
              dataKey="mer"
              name="MER"
              stroke={MARKETING_DATA_VIZ_SERIES.mer}
              strokeWidth={2.5}
              dot={false}
              activeDot={{ r: 4, strokeWidth: 0 }}
              animationDuration={animate ? 700 : 0}
            />
            <Line
              type="monotone"
              dataKey="metaRoas"
              name="Meta ROAS"
              stroke={MARKETING_DATA_VIZ_SERIES.metaRoas}
              strokeWidth={2}
              strokeDasharray="6 4"
              dot={false}
              activeDot={{ r: 4, strokeWidth: 0 }}
              animationDuration={animate ? 700 : 0}
            />
          </ComposedChart>
        )
      case 'comparison':
        return (
          <BarChart data={channelData ?? []} layout="vertical" margin={margin} accessibilityLayer throttleDelay={BLOG_CHART_THROTTLE_MS}>
            <defs>
              <linearGradient id="explorer-cpa-bar" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor={MARKETING_DATA_VIZ_SERIES.primary} stopOpacity={0.85} />
                <stop offset="100%" stopColor={MARKETING_DATA_VIZ_SERIES.metaRoas} stopOpacity={1} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke={AXIS_TOKENS.grid} strokeDasharray="3 3" horizontal={false} />
            <XAxis type="number" stroke={AXIS_TOKENS.stroke} fontSize={isMobile ? 10 : 12} />
            <YAxis type="category" dataKey="channel" stroke={AXIS_TOKENS.stroke} fontSize={isMobile ? 9 : 11} width={isMobile ? 88 : 120} />
            <Tooltip content={tooltip as TooltipContentType<ValueType, NameType>} />
            <Bar dataKey="cpa" name="CPA ($)" fill="url(#explorer-cpa-bar)" radius={[0, 4, 4, 0]} animationDuration={animate ? 600 : 0}>
              <LabelList
                dataKey="cpa"
                position="right"
                formatter={formatBarEndCurrency}
                {...BLOG_CHART_LABEL_STYLE}
              />
            </Bar>
          </BarChart>
        )
      case 'funnel':
        return <MarketingFunnelChart data={funnelStages ?? undefined} isActive={animate} />
      case 'retention':
        return <RetentionCurveChart data={retentionData ?? []} isActive={isActive} showLegend />
      case 'scatter':
        return (
          <ScatterChart margin={margin} accessibilityLayer throttleDelay={BLOG_CHART_THROTTLE_MS}>
            <CartesianGrid stroke={AXIS_TOKENS.grid} strokeDasharray="3 3" />
            <XAxis type="number" dataKey="spend" name="Spend" stroke={AXIS_TOKENS.stroke} fontSize={isMobile ? 10 : 12} label={{ value: isMobile ? 'Spend ($k)' : 'Weekly spend ($k)', position: 'insideBottom', offset: -8, fill: AXIS_TOKENS.stroke, fontSize: isMobile ? 10 : 12 }} />
            <YAxis type="number" dataKey="iroas" name="iROAS" stroke={AXIS_TOKENS.stroke} fontSize={isMobile ? 10 : 12} domain={[0, 5.5]} label={isMobile ? undefined : { value: 'iROAS', angle: -90, position: 'insideLeft', fill: AXIS_TOKENS.stroke }} />
            <ZAxis type="category" dataKey="campaign" name="Campaign" />
            <Tooltip cursor={{ strokeDasharray: '3 3' }} content={tooltip as TooltipContentType<ValueType, NameType>} />
            <Scatter
              data={scatterData ?? []}
              fill={MARKETING_DATA_VIZ_SERIES.secondary}
              stroke={MARKETING_DATA_VIZ_SERIES.primary}
              strokeWidth={1.5}
              animationDuration={animate ? 600 : 0}
            />
          </ScatterChart>
        )
      case 'cohort':
        return (
          <div className="flex h-full flex-col justify-center" role="grid" aria-label="Cohort repeat-rate heatmap">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[320px] border-collapse text-xs sm:text-sm">
                <thead>
                  <tr>
                    <th className="px-2 py-2 text-left text-gray-400">Cohort</th>
                    {cohortMatrix.months.map(month => (
                      <th key={month} className="px-2 py-2 text-center text-gray-400">M{month}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {cohortMatrix.cohorts.map(cohort => (
                    <tr key={cohort} role="row">
                      <th scope="row" className="px-2 py-2 text-left font-medium text-gray-200">{cohort}</th>
                      {cohortMatrix.months.map(month => {
                        const cell = cohortData?.find(entry => entry.cohort === cohort && entry.month === month)
                        const rate = cell?.repeatRate ?? 0
                        const colors = cohortRateToColor(rate)
                        const label = `${cohort} month ${month}: ${(rate * 100).toFixed(1)}% repeat rate`
                        return (
                          <td key={`${cohort}-${month}`} className="px-1 py-1" role="gridcell">
                            <div
                              className={cn('rounded-md px-2 py-2 text-center font-medium', colors.textClassName)}
                              style={{ backgroundColor: colors.backgroundColor }}
                              aria-label={label}
                            >
                              {(rate * 100).toFixed(0)}%
                            </div>
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      default: {
        const exhaustive: never = selected
        return <div>{exhaustive}</div>
      }
    }
  }

  const panelId = `chart-selection-panel-${selected}`

  return (
    <BlogChartShell
      title="Chart Selection Matrix"
      description="Pick the analytics question first — the chart family follows."
      gradient="from-cyan-400 to-indigo-400"
    >
      <div role="tablist" aria-label="Metric question chart families" className="mb-4 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
        {METRIC_QUESTIONS.map(question => (
          <button
            key={question.id}
            id={`chart-tab-${question.id}`}
            type="button"
            role="tab"
            aria-selected={selected === question.id}
            aria-controls={panelId}
            tabIndex={selected === question.id ? 0 : -1}
            aria-label={`${question.chartType}: ${question.question}`}
            onClick={() => handleSelect(question.id)}
            onKeyDown={event => handleTabKeyDown(event, question.id)}
            className={cn(
              'rounded-full border px-3 py-1.5 text-xs font-medium transition sm:text-sm',
              selected === question.id
                ? 'border-cyan-400/60 bg-cyan-400/15 text-cyan-100'
                : 'border-white/10 bg-white/5 text-gray-300 hover:border-white/20 hover:bg-white/10'
            )}
          >
            {question.chartType}
          </button>
        ))}
      </div>

      <div className="mb-4 rounded-2xl border border-white/10 bg-white/5 p-4">
        <p className="text-sm font-medium text-white">{activeQuestion.question}</p>
        <p className="mt-1 text-sm text-gray-400">
          Recommended: <span className="text-cyan-300">{activeQuestion.chartType}</span> — {activeQuestion.example}
        </p>
      </div>

      <div
        id={panelId}
        role="tabpanel"
        aria-labelledby={`chart-tab-${selected}`}
        className="relative h-[380px] w-full sm:h-[440px]"
      >
        <BlogChartPlotArea className="h-full">
          {selected === 'cohort' || selected === 'retention' || selected === 'funnel' ? (
            <div className="h-full">{renderChart()}</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%" debounce={300} initialDimension={{ width: 800, height: 480 }}>
              {renderChart()}
            </ResponsiveContainer>
          )}
        </BlogChartPlotArea>
      </div>
    </BlogChartShell>
  )
}

export default function ChartSelectionExplorer(): JSX.Element {
  return (
    <ChartVisibilityBoundary aria-label="Interactive chart selection matrix for marketing metrics">
      {({ isIntersecting }) => (
        <ChartSelectionExplorerContent isActive={isIntersecting} />
      )}
    </ChartVisibilityBoundary>
  )
}
